<template>
  <section v-if="project">
    <article class="card">
      <div class="card__padding">
        <h3 class="title__h3 bold">
          {{ t('literal.project_info') }}
        </h3>

        <form-notification
          class="margin__t--12"
          :keys="error"
        />

        <fieldset class="margin__t--18">
          <label
            for="name"
            class="input__label input__label--small"
          >
            {{ t('literal.title') }}
          </label>

          <input-text
            id="name"
            class="margin__t--6"
            v-model="clonedProject.name"
            :v="v$.name"
          />
        </fieldset>

        <fieldset class="margin__t--18">
          <label
            class="input__label input__label--small"
            for="image"
          >
            {{ t('literal.cover_image') }}
          </label>

          <app-image-preview
            id="image"
            class="margin__t--6"
            v-model="cover"
            :width="900"
            :height="600"
            accept="image/gif, image/jpeg, image/png"
            :upload="false"
            :deletable="false"
          />
        </fieldset>

        <fieldset class="margin__t--18">
          <label
            for="video"
            class="input__label input__label--small"
          >
            {{ t('literal.link_to_video') }} (Youtube, Vimeo)
          </label>

          <input-url
            id="video"
            class="margin__t--6"
            v-model="clonedProject.video_url"
            :v="v$.video_url"
          />
        </fieldset>

        <app-save-button
          class="margin__t--24"
          name="save_general_infos"
          :is-loading="loader"
          :is-disabled="loader || deleteLoader"
          :is-saved="!isUnsaved"
          @click="handleClickSave()"
        />
      </div>
    </article>

    <article
      class="card margin__t--24"
      v-if="members?.isProjectAdmin(authUser)"
    >
      <div class="card__main">
        <h3 class="title__h3 bold">
          {{ t('literal.delete_project') }}
        </h3>

        <form-notification
          class="margin__t--12"
          :keys="deleteError"
        />

        <p class="margin__t--18">
          {{ t('project.settings_delete_description') }}
        </p>

        <app-save-button
          class="btn__color--danger margin__t--24"
          name="delete_project"
          :is-loading="deleteLoader"
          :is-disabled="loader"
          @click="handleDeleteProject()"
        >
          {{ t('literal.delete_project') }}

          <template #icon>
            <fa-icon :icon="['fal', 'trash']" />
          </template>
        </app-save-button>
      </div>
    </article>

    <modal-confirm
      v-if="show_unsaved_modal"
      @close="cancelLeavingPage()"
      @confirm="leavePage()"
    >
      <template #confirm-content>
        <div class="align--center">
          <p>{{ t('literal.leave_page_without_saving') }}</p>
        </div>
      </template>
    </modal-confirm>
  </section>
</template>

<script setup>
import { computed, defineAsyncComponent } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { get as _get, isEqual as _isEqual, pick as _pick } from 'lodash'
import { useHead } from '@unhead/vue'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, maxLength } from '@vuelidate/validators'
import { useNotificationStore } from '@/stores/NotificationStore'
import { useUserStore } from '@/stores/UserStore'
import { useProjectStore } from '@/stores/ProjectStore'
import { useAuthenticationStore } from '@/stores/AuthenticationStore'
import { useModalStore } from '@/stores/ModalStore'
import { useTrackingProject } from '@/vendors/tracking'
import { useI18n } from '@/vendors/i18n'
import { useUnsaved } from '@/composables/app/useUnsaved'
import { validVideoUrl } from '@/utils/validators'
import { scrollTo, resolvePromisesSequentially } from '@/utils/utils'

import InputText from '&/atoms/InputText'
import InputUrl from '&/atoms/InputUrl'
import AppImagePreview from '&/organisms/AppImagePreview'
import AppSaveButton from '&/molecules/AppSaveButton'
const FormNotification = defineAsyncComponent(() => import('&/molecules/FormNotification'))
const ModalConfirm = defineAsyncComponent(() => import('&/organisms/ModalConfirm'))

const { projectSlug } = defineProps({ projectSlug: { type: String, required: true } })

const { t } = useI18n()
const { push } = useRouter()
const { trackProjectDeleted } = useTrackingProject()
const { postSideNotification } = useNotificationStore()
const { postConfirmModal } = useModalStore()
const { authUser } = storeToRefs(useAuthenticationStore())
const { $resetStore: $resetUserStore } = useUserStore()
const { getProject, getProjectMembers, patchProject, patchProjectImage, deleteProject, $resetStore: $resetProjectStore } = useProjectStore()
const { loader: deleteLoader, error: deleteError } = storeToRefs(useProjectStore())

const { project, clonedProject, loader, error } = getProject({ id: projectSlug })
const { members } = getProjectMembers({ project_id: projectSlug })

useHead({ title: computed(() => `${t('literal.general')} / ${t('literal.settings')} / ${project.value?.name}`) })
const v$ = useVuelidate({ name: { required, minLength: minLength(3), maxLength: maxLength(50) }, video_url: { validVideoUrl } }, clonedProject)
const condition = computed(() => !_isEqual(project.value.to_general_settings, clonedProject.value.to_general_settings))
const { show_unsaved_modal, isUnsaved, cancelLeavingPage, leavePage } = useUnsaved({ condition })

const formData = new FormData()

const cover = computed({
  get: () => _get(clonedProject.value, 'image.url') || _get(clonedProject.value, 'campaign.image.url'),
  set: file => {
    clonedProject.value.image.url = file?.toBlobURL()

    formData.delete('project[image]')

    if (file) formData.append('project[image]', file)
  }
})

const handleClickSave = () => {
  v$.value.$touch()

  if (v$.value.$error) return scrollTo('.input--errors')

  const promises = [patchProject.bind(null, { id: project.value.slug, params: _pick(clonedProject.value, ['name', 'video_url']) })]

  if (formData.get('project[image]')) promises.unshift(patchProjectImage.bind(null, { id: project.value.slug, params: formData }))

  resolvePromisesSequentially(promises).then(() => {
    $resetUserStore('projects')
    postSideNotification({ text: 'literal.modifications_have_been_saved' })
  })
}

const handleDeleteProject = () => postConfirmModal({
  title: 'project.settings_delete_confirmation_msg',
  callback: async () => {
    try {
      await deleteProject({ id: project.value.slug })
      trackProjectDeleted({ project: project.value })
      await push({ name: 'Home' })
      $resetProjectStore(['projects', 'project'])
      $resetUserStore('projects')
    } catch (e) {
      deleteError.value = e.data?.errors?.length ? e.data.errors : 'failed_to_delete_project'
    }
  }
})
</script>
